import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2, Loader2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from './alert-dialog';
import { Button } from '@/components/atoms/button';
import { cn } from '@/lib/utils';

interface DeleteConfirmDialogProps {
  title?: string;
  description?: string;
  itemName?: string;
  onConfirm: () => void;
  isLoading?: boolean;
  trigger?: React.ReactNode;
  variant?: 'icon' | 'button';
  disabled?: boolean;
}

export function DeleteConfirmDialog({
  title,
  description,
  itemName,
  onConfirm,
  isLoading = false,
  trigger,
  variant = 'icon',
  disabled = false,
}: DeleteConfirmDialogProps) {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);

  const handleConfirm = () => {
    onConfirm();
    // Don't close immediately, let the parent handle it
  };

  const defaultTitle = title || t('common.deleteConfirmTitle', 'Confirmer la suppression');
  const defaultDescription =
    description ||
    (itemName
      ? t('common.deleteConfirmDescriptionWithName', {
          name: itemName,
          defaultValue: `Êtes-vous sûr de vouloir supprimer "${itemName}" ? Cette action est irréversible.`,
        })
      : t('common.deleteConfirmDescription', 'Êtes-vous sûr de vouloir supprimer cet élément ? Cette action est irréversible.'));

  const defaultTrigger =
    variant === 'icon' ? (
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8 text-muted-foreground hover:text-destructive"
        disabled={disabled}
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    ) : (
      <Button variant="destructive" size="sm" disabled={disabled}>
        <Trash2 className="h-4 w-4 mr-2" />
        {t('common.delete', 'Supprimer')}
      </Button>
    );

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild onClick={(e) => e.stopPropagation()}>
        {trigger || defaultTrigger}
      </AlertDialogTrigger>
      <AlertDialogContent onClick={(e) => e.stopPropagation()}>
        <AlertDialogHeader>
          <AlertDialogTitle>{defaultTitle}</AlertDialogTitle>
          <AlertDialogDescription>{defaultDescription}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isLoading}>
            {t('common.cancel', 'Annuler')}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={isLoading}
            className={cn('bg-destructive text-destructive-foreground hover:bg-destructive/90')}
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {t('common.deleting', 'Suppression...')}
              </>
            ) : (
              <>
                <Trash2 className="h-4 w-4 mr-2" />
                {t('common.delete', 'Supprimer')}
              </>
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
